import { useState, useRef, useEffect } from 'react'
import toast from 'react-hot-toast'
import { RiMapPinLine, RiSearchLine } from 'react-icons/ri'
import { useModalStore } from '@/stores/useModalStore'
import { useCreateAddress, useUpdateAddress } from '@/api/useAddresses'
import { useGoogleMaps } from '@/components/providers/GoogleMapsProvider'
import type { Address } from '@/api/useAddresses'
import { Button } from '@/components/ui'
import styles from './AddressFormModal.module.css'

const DEPARTMENTS: Record<string, string> = {
    'amazonas': 'Amazonas', 'ancash': 'Áncash', 'apurimac': 'Apurímac',
    'arequipa': 'Arequipa', 'ayacucho': 'Ayacucho', 'cajamarca': 'Cajamarca',
    'callao': 'Callao', 'cusco': 'Cusco', 'huancavelica': 'Huancavelica',
    'huanuco': 'Huánuco', 'ica': 'Ica', 'junin': 'Junín',
    'la_libertad': 'La Libertad', 'lambayeque': 'Lambayeque', 'lima': 'Lima',
    'loreto': 'Loreto', 'madre_de_dios': 'Madre de Dios', 'moquegua': 'Moquegua',
    'pasco': 'Pasco', 'piura': 'Piura', 'puno': 'Puno',
    'san_martin': 'San Martín', 'tacna': 'Tacna', 'tumbes': 'Tumbes',
    'ucayali': 'Ucayali',
}

const DEPARTMENT_ENTRIES = Object.entries(DEPARTMENTS)

const GOOGLE_TO_SLUG: Record<string, string> = {
    'amazonas': 'amazonas', 'ancash': 'ancash', 'áncash': 'ancash',
    'apurímac': 'apurimac', 'apurimac': 'apurimac', 'arequipa': 'arequipa',
    'ayacucho': 'ayacucho', 'cajamarca': 'cajamarca', 'callao': 'callao',
    'provincia constitucional del callao': 'callao', 'cusco': 'cusco',
    'cuzco': 'cusco', 'huancavelica': 'huancavelica', 'huánuco': 'huanuco',
    'huanuco': 'huanuco', 'ica': 'ica', 'junín': 'junin', 'junin': 'junin',
    'la libertad': 'la_libertad', 'lambayeque': 'lambayeque', 'lima': 'lima',
    'lima region': 'lima', 'lima metropolitana': 'lima', 'loreto': 'loreto',
    'madre de dios': 'madre_de_dios', 'moquegua': 'moquegua', 'pasco': 'pasco',
    'piura': 'piura', 'puno': 'puno', 'san martín': 'san_martin',
    'san martin': 'san_martin', 'tacna': 'tacna', 'tumbes': 'tumbes',
    'ucayali': 'ucayali',
}

interface Prediction {
    placeId: string
    main: string
    secondary: string
}

interface AddressFormModalProps {
    address?: Address
    handleClose?: () => void
}

export function AddressFormModal({ address }: AddressFormModalProps) {
    const { closeModal } = useModalStore()
    const { isLoaded } = useGoogleMaps()
    const createAddress = useCreateAddress()
    const updateAddress = useUpdateAddress()

    const isEdit = !!address

    const [label, setLabel] = useState(address?.label ?? '')
    const [department, setDepartment] = useState(address?.department ?? '')
    const [province, setProvince] = useState(address?.province ?? '')
    const [district, setDistrict] = useState(address?.district ?? '')
    const [addressLine, setAddressLine] = useState(address?.address_line ?? '')
    const [reference, setReference] = useState(address?.reference ?? '')
    const [phone, setPhone] = useState(address?.phone ?? '')
    const [isDefault, setIsDefault] = useState(address?.is_default ?? false)
    const [markerPos, setMarkerPos] = useState<google.maps.LatLngLiteral | null>(
        address?.latitude != null && address?.longitude != null
            ? { lat: address.latitude, lng: address.longitude }
            : null
    )
    const [googleVerified, setGoogleVerified] = useState(!!address)
    const [showUnverifiedWarning, setShowUnverifiedWarning] = useState(false)

    const [searchQuery, setSearchQuery] = useState('')
    const [predictions, setPredictions] = useState<Prediction[]>([])
    const [showDropdown, setShowDropdown] = useState(false)

    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const mapRef = useRef<HTMLDivElement>(null)
    const mapInstanceRef = useRef<google.maps.Map | null>(null)
    const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null)

    const isPending = createAddress.isPending || updateAddress.isPending

    // Render map with AdvancedMarkerElement
    useEffect(() => {
        if (!isLoaded || !markerPos || !mapRef.current) return

        if (!mapInstanceRef.current) {
            mapInstanceRef.current = new google.maps.Map(mapRef.current, {
                center: markerPos,
                zoom: 16,
                disableDefaultUI: true,
                zoomControl: true,
                mapId: 'address-map',
            })
        } else {
            mapInstanceRef.current.setCenter(markerPos)
        }

        if (markerRef.current) {
            markerRef.current.position = markerPos
        } else if (google.maps.marker?.AdvancedMarkerElement) {
            markerRef.current = new google.maps.marker.AdvancedMarkerElement({
                map: mapInstanceRef.current,
                position: markerPos,
                gmpDraggable: true,
            })
            markerRef.current.addListener('dragend', (event: google.maps.MapMouseEvent) => {
                const pos = event.latLng
                if (pos) {
                    setMarkerPos({ lat: pos.lat(), lng: pos.lng() })
                }
            })
        }
    }, [isLoaded, markerPos])

    const fetchPredictions = async (input: string) => {
        if (!isLoaded || input.length < 3) {
            setPredictions([])
            setShowDropdown(false)
            return
        }

        try {
            const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input,
                includedRegionCodes: ['pe'],
            })

            const items: Prediction[] = suggestions
                .filter((s) => s.placePrediction)
                .map((s) => {
                    const p = s.placePrediction!
                    return {
                        placeId: p.placeId,
                        main: p.mainText?.text ?? '',
                        secondary: p.secondaryText?.text ?? '',
                    }
                })

            setPredictions(items)
            setShowDropdown(items.length > 0)
        } catch {
            setPredictions([])
            setShowDropdown(false)
        }
    }

    const handleSearchChange = (value: string) => {
        setSearchQuery(value)
        setGoogleVerified(false)
        setShowUnverifiedWarning(false)

        if (debounceTimer.current) clearTimeout(debounceTimer.current)
        debounceTimer.current = setTimeout(() => fetchPredictions(value), 300)
    }

    const selectPrediction = async (prediction: Prediction) => {
        setSearchQuery(prediction.main + ', ' + prediction.secondary)
        setShowDropdown(false)
        setPredictions([])

        try {
            const place = new google.maps.places.Place({ id: prediction.placeId })
            await place.fetchFields({ fields: ['addressComponents', 'location', 'formattedAddress'] })

            const loc = place.location
            if (loc) {
                setMarkerPos({ lat: loc.lat(), lng: loc.lng() })
            }
            setGoogleVerified(true)
            setShowUnverifiedWarning(false)

            // Reset fields before filling to ensure update
            setDepartment('')
            setProvince('')
            setDistrict('')
            setAddressLine('')

            let streetNumber = ''
            let route = ''
            let locality = ''
            let adminArea2 = ''
            let adminArea1 = ''

            for (const component of place.addressComponents ?? []) {
                const types = component.types
                if (types.includes('street_number')) {
                    streetNumber = component.longText ?? ''
                } else if (types.includes('route')) {
                    route = component.longText ?? ''
                } else if (types.includes('locality')) {
                    locality = component.longText ?? ''
                } else if (types.includes('administrative_area_level_2')) {
                    adminArea2 = component.longText ?? ''
                } else if (types.includes('administrative_area_level_1')) {
                    adminArea1 = component.longText ?? ''
                }
            }

            const deptSlug = GOOGLE_TO_SLUG[adminArea1.toLowerCase()]
            if (deptSlug) setDepartment(deptSlug)

            if (adminArea2) setProvince(adminArea2)

            if (locality && adminArea2) {
                setDistrict(locality)
            } else if (locality) {
                setProvince(locality)
            }

            const street = [route, streetNumber].filter(Boolean).join(' ')
            if (street) {
                setAddressLine(street)
            } else if (place.formattedAddress) {
                const parts = place.formattedAddress.split(',').map(s => s.trim())
                setAddressLine(parts.slice(0, Math.max(1, parts.length - 2)).join(', '))
            }
        } catch {
            toast.error('No se pudo obtener los detalles de la dirección')
        }
    }

    const saveAddress = () => {
        const payload = {
            label: label.trim(),
            department,
            province: province.trim(),
            district: district.trim(),
            address_line: addressLine.trim(),
            reference: reference.trim(),
            phone: phone.trim(),
            is_default: isDefault,
            latitude: markerPos?.lat ?? null,
            longitude: markerPos?.lng ?? null,
        }

        if (isEdit && address) {
            updateAddress.mutate(
                { id: address.id, ...payload },
                {
                    onSuccess: () => {
                        toast.success('Dirección actualizada')
                        closeModal()
                    },
                    onError: () => toast.error('Error al actualizar la dirección'),
                }
            )
        } else {
            createAddress.mutate(payload, {
                onSuccess: () => {
                    toast.success('Dirección agregada')
                    closeModal()
                },
                onError: () => toast.error('Error al guardar la dirección'),
            })
        }
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        if (!label.trim() || !department || !province.trim() || !district.trim() || !addressLine.trim() || !phone.trim()) {
            toast.error('Completa todos los campos requeridos')
            return
        }

        if (isLoaded && !googleVerified && !showUnverifiedWarning) {
            setShowUnverifiedWarning(true)
            return
        }

        saveAddress()
    }

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>
                {isEdit ? 'Editar dirección' : 'Nueva dirección'}
            </h3>

            <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.field}>
                    <label className={styles.label} htmlFor="addr-label">Nombre *</label>
                    <input
                        id="addr-label"
                        type="text"
                        className={styles.input}
                        placeholder="Ej. Casa, Oficina"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        required
                    />
                </div>

                <div className={styles.field}>
                    <label className={styles.label} htmlFor="addr-search">
                        <RiMapPinLine aria-hidden="true" /> Dirección *
                    </label>
                    <div className={styles.autocompleteWrapper}>
                        <div className={styles.searchInputWrapper}>
                            <RiSearchLine className={styles.searchIcon} aria-hidden="true" />
                            <input
                                id="addr-search"
                                type="text"
                                className={styles.searchInput}
                                placeholder="Busca tu dirección..."
                                value={searchQuery}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                onFocus={() => { if (predictions.length > 0) setShowDropdown(true) }}
                                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                                autoComplete="off"
                            />
                        </div>
                        {showDropdown && predictions.length > 0 && (
                            <ul className={styles.dropdown}>
                                {predictions.map((p) => (
                                    <li
                                        key={p.placeId}
                                        className={styles.dropdownItem}
                                        onMouseDown={() => selectPrediction(p)}
                                    >
                                        <RiMapPinLine className={styles.dropdownIcon} />
                                        <div>
                                            <span className={styles.dropdownMain}>{p.main}</span>
                                            <span className={styles.dropdownSecondary}>{p.secondary}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Map rendered with new API */}
                {markerPos && (
                    <div className={styles.mapWrapper}>
                        <div
                            ref={mapRef}
                            className={styles.map}
                            role="application"
                            aria-label="Mapa de la dirección. Arrastra el marcador para ajustar la ubicación."
                        />
                    </div>
                )}

                <div className={styles.row}>
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="addr-department">Departamento *</label>
                        <select
                            id="addr-department"
                            className={styles.input}
                            value={department}
                            onChange={(e) => setDepartment(e.target.value)}
                            required
                        >
                            <option value="">Seleccionar</option>
                            {DEPARTMENT_ENTRIES.map(([value, lbl]) => (
                                <option key={value} value={value}>{lbl}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="addr-province">Provincia *</label>
                        <input
                            id="addr-province"
                            type="text"
                            className={styles.input}
                            placeholder="Provincia"
                            value={province}
                            onChange={(e) => setProvince(e.target.value)}
                            required
                        />
                    </div>
                </div>

                <div className={styles.row}>
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="addr-district">Distrito *</label>
                        <input
                            id="addr-district"
                            type="text"
                            className={styles.input}
                            placeholder="Distrito"
                            value={district}
                            onChange={(e) => setDistrict(e.target.value)}
                            required
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="addr-phone">Teléfono *</label>
                        <input
                            id="addr-phone"
                            type="tel"
                            className={styles.input}
                            placeholder="Ej. 987654321"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            required
                        />
                    </div>
                </div>

                <div className={styles.field}>
                    <label className={styles.label} htmlFor="addr-reference">
                        Referencia <span className={styles.optional}>(opcional)</span>
                    </label>
                    <input
                        id="addr-reference"
                        type="text"
                        className={styles.input}
                        placeholder="Ej. Frente al parque"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                    />
                </div>

                <label className={styles.checkboxLabel}>
                    <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={isDefault}
                        onChange={(e) => setIsDefault(e.target.checked)}
                    />
                    <span>Establecer como dirección predeterminada</span>
                </label>

                {showUnverifiedWarning && (
                    <div className={styles.warning}>
                        Google no encontró tu dirección. ¿Estás seguro de que deseas continuar?
                    </div>
                )}

                <div className={styles.actions}>
                    <Button variant="secondary" type="button" onClick={closeModal} disabled={isPending}>
                        Cancelar
                    </Button>
                    <Button variant="primary" type="submit" disabled={isPending}>
                        {isPending ? 'Guardando...' : showUnverifiedWarning ? 'Sí, guardar' : isEdit ? 'Guardar cambios' : 'Agregar dirección'}
                    </Button>
                </div>
            </form>
        </div>
    )
}
